/* global document */
/* global location */

import { browserHistory } from 'react-router';
import _ from 'lodash';

import {
  updateExtraCosts,
  updateCurrentUser,
  fetchVouchers,
  reloadCreditCards,
  fetchUserOrders,
  TimeHelpers,
  RestaurantHelpers } from '@arreeba/ristoranti-frontend-lib';

import {
  LOAD_APP_CONFIGURATION_SUCCESS,
  DELIVERY_ADDRESS_CHECK_SUCCESS,
  STORE_ORDER_SUCCESS,
  STORE_ORDER_FAILURE,
  MENU_CART_ADDITEM,
  MENU_CART_REMOVEITEM,
} from '@arreeba/ristoranti-frontend-lib/lib/constants';

import {
  BOOKING_REQUEST_CREATE_FAILURE,
  BOOKING_REQUEST_CREATE_SUCCESS,
  USER_AUTHENTICATE_SUCCESS,
  USER_AUTHENTICATE_FAILURE,
  USER_UPDATE_ACCESSTOKEN,
  USER_REGISTER_FAILURE,
  USER_REGISTER_SUCCESS,
} from '@arreeba/ristoranti-lib/lib/constants';

import {
  serviceSelector,
  mapDispatchToProps,
  getTheme,
} from '../util';

import Url from '../lib/url';

const createCallback = obj => {
  return Object.keys(obj).reduce((acc, key) => {
    const callback = obj[key];
    const listener = callback instanceof Array ? callback : [callback];

    return {
      ...acc,
    [key]: (...args) => listener.map(fn => fn(...args)),
    };
  }, { });
};


const loadBookingAvailability = (state, services, actions) => {
  const appId = state.app.id;
  // load booking availability for restaurants and booking rushes
  const rangeAmp = state.app.config.booking.availabilityRange || 7;
  const range = TimeHelpers.nextXDaysRange(rangeAmp);
  services.booking.loadBookingAvailability(
    appId,
    { from: range[0], to: range[1] },
    actions.booking,
  );
};

const deliveryAddressCheckSuccess = (state, services, actions, action) => {
  if (action.data && action.data.id) {
    const hasOnline = RestaurantHelpers.hasOnlineService(
      _.find(state.app.merchant.restaurants, { id: action.data.id }),
      'delivery',
    );
    if (!hasOnline) {
      // address in a delivery area, but restaurant has the service offline
      browserHistory.push(
        Url.orderNoDelivery(Url.getAppIdFromUrl(location), false),
      );
      return;
    }

    browserHistory.push(
      Url.orderDeliveryRestaurantFromRoute(location.pathname, action.data.id),
    );
    return;
  }

  // redirect to no-delivery route
  browserHistory.push(
    Url.orderNoDelivery(Url.getAppIdFromUrl(location)),
  );
};

const updateWidgetExtraCosts = (state, services, actions, action) => {
  // the target restaurant id is in the url not in the state
  // as for the mobile app
  const augmentedState = {
    ...state,
  };
  augmentedState.order.targetRestaurant = Url.getTargetRestaurantIdFromUrl(location);
  updateExtraCosts(augmentedState, services, actions, action);
};

const storeOrderError = (state, services, actions, action) => {
  actions.ux.setMessage({
    title: 'Errore',
    text: 'Si è verificato un problema con il pagamento',
    type: 'error',
  });
};

const redirectToOrderEnd = (state, services, actions, action) => {
  browserHistory.push(Url.orderEndFromRoute(location.pathname));
};

const redirectToBookingEnd = (state, services, actions, action) => {
  browserHistory.push(Url.bookingEndFromRoute(location.pathname));
};

const showErrors = (state, service, actions, action) => {
  const errors = state.user.errors;
  if (errors && errors.length > 0) {
    if (errors[0].responseLevel === 'USER_FAILURE') {
      actions.ux.setMessage({
        title: 'Errore',
        text: errors[0].message,
        type: 'error',
      });
    }
  }
};

const createCustomStyles = (state, services, actions, action) => {
  const theme = getTheme(state);
  const primary = theme.widget ? theme.widget.primary : theme.primary;
  const accent = theme.widget ? theme.widget.accent : theme.accent;
  const secondary = theme.widget ? theme.widget.secondary : theme.secondary;

  if (!primary) return;

  const styleEl = document.createElement('style');
  document.head.appendChild(styleEl);

  const styleSheet = styleEl.sheet;
  styleSheet.insertRule(`a, h1 { color: ${primary}; }`, styleSheet.cssRules.length);
  styleSheet.insertRule(`.primary { color: ${primary} !important; }`, styleSheet.cssRules.length);
  styleSheet.insertRule(`.primary--background { background-color: ${primary} !important; }`, styleSheet.cssRules.length);
  styleSheet.insertRule(`.primary--border { border-color: ${primary} !important; }`, styleSheet.cssRules.length);
  styleSheet.insertRule(`.primary--border-top { border-top-color: ${primary} !important; }`, styleSheet.cssRules.length);
  styleSheet.insertRule(`.secondary { color: ${secondary} !important; }`, styleSheet.cssRules.length);
  styleSheet.insertRule(`.btn-submit { background-color: ${primary}; }`, styleSheet.cssRules.length);
  styleSheet.insertRule(`.btn-secondary { background-color: ${secondary}; }`, styleSheet.cssRules.length);
  styleSheet.insertRule(`.box-list li.selected { background-color: ${primary}; }`, styleSheet.cssRules.length);
  styleSheet.insertRule(`.number-picker input:hover { color: ${primary}; }`, styleSheet.cssRules.length);
  styleSheet.insertRule(`form.form-stacked input:not([type="submit"]):focus { border-color: ${primary}; }`, styleSheet.cssRules.length);

  styleSheet.insertRule(`.accent { color: ${accent} !important; }`, styleSheet.cssRules.length);
  styleSheet.insertRule(`.accent--background { background-color: ${accent} !important; }`, styleSheet.cssRules.length);
};

const loaders = createCallback({
  'booking-start': (state, services, actions) => actions.booking.endBooking(),
  'booking-select-date': loadBookingAvailability,
});

const eventListeners = createCallback({
  [LOAD_APP_CONFIGURATION_SUCCESS]: createCustomStyles,
  [DELIVERY_ADDRESS_CHECK_SUCCESS]: deliveryAddressCheckSuccess,
  [USER_REGISTER_FAILURE]: showErrors,
  [USER_AUTHENTICATE_FAILURE]: showErrors,
  [USER_AUTHENTICATE_SUCCESS]: [
    reloadCreditCards,
    fetchUserOrders,
    fetchVouchers,
  ],
  [USER_REGISTER_SUCCESS]: [
    reloadCreditCards,
    fetchVouchers,
  ],
  [USER_UPDATE_ACCESSTOKEN]: [
    updateCurrentUser,
    reloadCreditCards,
    fetchVouchers,
  ],
  [STORE_ORDER_SUCCESS]: [redirectToOrderEnd],
  [STORE_ORDER_FAILURE]: [storeOrderError],
  [MENU_CART_ADDITEM]: [updateWidgetExtraCosts],
  [MENU_CART_REMOVEITEM]: [updateWidgetExtraCosts],
  [BOOKING_REQUEST_CREATE_SUCCESS]: [redirectToBookingEnd]
});


const defaultFn = () => null;

export default ({ dispatch, getState }) => {
  const actions = mapDispatchToProps(dispatch);

  return next => (action) => {
    next(action);

    const state = getState();
    const services = serviceSelector(state);

    if (action.type === 'route.mounted') {
      const fn = loaders[action.route] || defaultFn;
      fn(state, services, actions);
    }

    if (action.type === BOOKING_REQUEST_CREATE_FAILURE &&
        action.errors[0].responseLevel === 'USER_FAILURE') {
      actions.ux.setMessage({
        title: 'Errore',
        text: action.errors[0].message,
        type: 'error',
      });
    }

    const listener = eventListeners[action.type] || defaultFn;
    listener(state, services, actions, action);
  };
};



// WEBPACK FOOTER //
// ./src/middleware/side-effects.js